import React, { useEffect, useState, useRef } from "react";
import Layout from "./Layout";
import BlogSideImage from "../../Components/Blogs/BlogSideImage";
import BlogOverlayImg from "../../Components/Blogs/BlogOverlayImg";
import styles from "./Blog.module.css";
import { getActiveClient, isPreviewMode } from "../../lib/sanity";
import PreviewBanner from "../../Components/PreviewBanner";
import Pagination from "../../Components/Blogs/HelperComponents/Pagination";
import { useLanguage } from "../../hooks/useLanguage";
import { useTranslation } from "react-i18next";

const BlogPage = () => {
  const { currentLang } = useLanguage();
  const { t } = useTranslation(["blog"]);
  const [allPosts, setAllPosts] = useState([]);
  const [featuredPost, setFeaturedPost] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 6;
  const [originalPosts, setOriginalPosts] = useState([]);
  const [showFeaturedPost, setShowFeaturedPost] = useState(true);
  const [categoryPosts, setCategoryPosts] = useState({});

  const categoryOptions = [
    { value: "Kategória", label: t("chrome.filterCategoryDefault") },
    { value: "Trendy v B2B", label: t("categories.b2bTrends", "Trendy v B2B") },
    { value: "AI", label: "AI" },
    { value: "Behaviorálne výskumy", label: t("categories.behavioralResearch", "Behaviorálne výskumy") },
    { value: "Reklama a kreatíva", label: t("categories.advertisingCreative", "Reklama a kreatíva") },
    { value: "Predaj", label: t("categories.sales", "Predaj") },
    { value: "Iné", label: t("categories.other", "Iné") },
  ];
  const categoryDisplayNames = categoryOptions.slice(1).map(({ value }) => value);
  const sortOptions = [
    { value: "Najnovšie", label: t("chrome.filterSortNewest") },
    { value: "Najstaršie", label: t("chrome.filterSortOldest") },
    { value: "Populárne", label: t("chrome.filterSortPopular") },
  ];

  const [selectedCategory, setSelectedCategory] = useState("Kategória");
  const [sortBy, setSortBy] = useState("Najnovšie");
  const [activeFilters, setActiveFilters] = useState([]);
  const userInitiatedAction = React.useRef(false);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const months = [
      "Január",
      "Február",
      "Marec",
      "Apríl",
      "Máj",
      "Jún",
      "Júl",
      "August",
      "September",
      "Október",
      "November",
      "December",
    ];
    return `${date.getDate()} ${months[date.getMonth()]}`;
  };

  // Group posts by category and get latest 3 from each
  const groupPostsByCategory = (posts) => {
    const grouped = {};

    categoryDisplayNames.forEach((category) => {
      const categoryPosts = posts
        .filter((post) => post._rawCategories.includes(category))
        .sort((a, b) => new Date(b._publishedAt) - new Date(a._publishedAt))
        .slice(0, 3);

      if (categoryPosts.length > 0) {
        grouped[category] = categoryPosts;
      }
    });

    return grouped;
  };

  useEffect(() => {
    if (!userInitiatedAction.current) {
      window.scrollTo(0, 0);
    }
    userInitiatedAction.current = false;
  }, []);

  useEffect(() => {
    setIsLoading(true);
    getActiveClient()
      .fetch(
        `*[_type == "post"] | order(publishedAt desc) {
          _id,
          title,
          excerpt,
          slug,
          language,
          localeVariants[]{
            language,
            title,
            slug,
            excerpt,
            status,
            indexable
          },
          tooltip,
          mainImage {
            asset->{
              _id,
              url
            },
            caption
          },
          publishedAt,
          featured,
          claps,
          "categories": categories[]->title,
          "author": author->{
            _id,
            name,
            "image": image.asset->url
          },
          body[]{
            ...,
            _type == "block" => {
              ...,
              children[]{
                ...,
                text,
                _type
              }
            }
          }
        }`
      )
      .then((data) => {
        try {
          const localizedData = data
            .map((post) => {
              const variant = (post.localeVariants || []).find(
                (item) =>
                  item.language === currentLang &&
                  item.status === "approved" &&
                  item.indexable !== false
              );
              if (!variant) return currentLang === "sk" ? post : null;
              return {
                ...post,
                title: variant.title || post.title,
                excerpt: variant.excerpt || post.excerpt,
                slug: variant.slug || post.slug,
                body: variant.body || post.body,
                seo: variant.seo || post.seo,
                locale: currentLang,
              };
            })
            .filter(Boolean);
          const featured = localizedData.find((post) => post.featured);

          const transformedFeatured = featured
            ? [
                {
                  _id: featured._id,
                  topLabel: "Odporúčame",
                  category: featured.categories || ["AI"],
                  tags: [],
                  blogType: "sideImage",
                  img: featured.mainImage?.asset.url || "/default-image.jpg",
                  title: featured.title || "",
                  content: featured.excerpt || "",
                  author: {
                    name: featured.author?.name || "",
                    image: featured.author?.image || "",
                    _id: featured.author?._id || "",
                  },
                  slug: {
                    current:
                      typeof featured.slug === "string"
                        ? featured.slug
                        : featured.slug?.current || "",
                  },
                  date: featured.publishedAt
                    ? formatDate(featured.publishedAt)
                    : "",
                  double_col: false,
                  _rawCategories: featured.categories || [],
                },
              ]
            : [];

          const transformedPosts = localizedData.filter((post) => !post.featured).map((post) => {
            const categoryObjects =
              post.categories?.map((category) => ({
                title: category,
                slug: {
                  current: category.toLowerCase().replace(/\s+/g, "-"),
                },
              })) || [];

            return {
              _id: post._id,
              category: categoryObjects,
              tags: [],
              blogType: "overlay",
              img: post.mainImage?.asset.url || "/default-image.jpg",
              title: post.title || "",
              content: post.excerpt || "",
              excerpt: post.excerpt || "",
              body: post.body || [],
              author: 0,
              likes: post.claps || 0,
              comments: 0,
              slug: {
                current:
                  typeof post.slug === "string"
                    ? post.slug
                    : post.slug?.current || "",
              },
              date: post.publishedAt || "",
              double_col: false,
              readTime: post.readTime,
              _publishedAt: post.publishedAt,
              _rawCategories: post.categories || [],
            };
          });

          // Group posts by category
          const grouped = groupPostsByCategory(transformedPosts);

          setFeaturedPost(transformedFeatured);
          setAllPosts(transformedPosts);
          setOriginalPosts(transformedPosts);
          setCategoryPosts(grouped);
        } catch (err) {
          console.error("Error processing post data:", err);
        } finally {
          setIsLoading(false);
        }
      })
      .catch((error) => {
        console.error("Error fetching posts:", error);
        setIsLoading(false);
      });
  }, [currentLang]);

  const scrollToFilteredResults = () => {
    setTimeout(() => {
      const blogContentList = document.getElementById("blog-content-list");
      if (blogContentList) {
        const offsetPixels = 48;
        const rect = blogContentList.getBoundingClientRect();
        const scrollTop = window.pageYOffset + rect.top - offsetPixels;
        window.scrollTo({
          top: scrollTop,
          behavior: "smooth",
        });
      }
    }, 100);
  };

  const handleCategoryChange = (option) => {
    const category = option.value;
    userInitiatedAction.current = true;
    setSelectedCategory(category);

    if (category !== "Kategória") {
      const featuredHasCategory =
        featuredPost &&
        featuredPost.length > 0 &&
        featuredPost[0]._rawCategories.includes(category);
      setShowFeaturedPost(featuredHasCategory);

      if (userInitiatedAction.current) {
        scrollToFilteredResults();
      }
    } else {
      setShowFeaturedPost(true);
    }

    const updatedFilters = activeFilters.filter(
      (filter) => filter.type !== "category"
    );

    if (category !== "Kategória") {
      updatedFilters.push({ type: "category", value: category });
    }

    setActiveFilters(updatedFilters);
    setCurrentPage(1);
  };

  const handleSortChange = (option) => {
    setSortBy(option.value);
  };

  const removeFilter = (filterToRemove) => {
    userInitiatedAction.current = true;
    const updatedFilters = activeFilters.filter(
      (filter) =>
        !(
          filter.type === filterToRemove.type &&
          filter.value === filterToRemove.value
        )
    );
    setActiveFilters(updatedFilters);

    if (filterToRemove.type === "category") {
      setSelectedCategory("Kategória");
      setShowFeaturedPost(true);
    }

    setCurrentPage(1);
  };

  const clearAllFilters = () => {
    userInitiatedAction.current = true;

    const hadFilters =
      selectedCategory !== "Kategória" || activeFilters.length > 0;

    setSelectedCategory("Kategória");
    setSortBy("Najnovšie");
    setActiveFilters([]);
    setCurrentPage(1);
    setShowFeaturedPost(true);
    if (hadFilters && userInitiatedAction.current) {
      scrollToFilteredResults();
    }
  };

  const getFilteredPosts = () => {
    let filtered = [...originalPosts];

    if (selectedCategory !== "Kategória") {
      filtered = filtered.filter((post) =>
        post._rawCategories.includes(selectedCategory)
      );
    }

    if (sortBy === "Najnovšie") {
      filtered.sort(
        (a, b) => new Date(b._publishedAt) - new Date(a._publishedAt)
      );
    } else if (sortBy === "Najstaršie") {
      filtered.sort(
        (a, b) => new Date(a._publishedAt) - new Date(b._publishedAt)
      );
    } else if (sortBy === "Populárne") {
      filtered.sort((a, b) => (b.likes || 0) - (a.likes || 0));
    }

    return filtered;
  };

  const filteredPosts = getFilteredPosts();
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = filteredPosts.slice(indexOfFirstPost, indexOfLastPost);

  const cleanPostsForComponent = (posts) => {
    return posts.map((post) => {
      return {
        _id: post._id,
        category: post.category,
        tags: post.tags || [],
        blogType: post.blogType,
        img: post.img,
        title: post.title,
        content: post.content,
        excerpt: post.excerpt,
        body: post.body,
        author: post.author,
        likes: post.likes,
        comments: post.comments,
        slug: post.slug,
        date: post.date,
        double_col: post.double_col,
        readTime: post.readTime,
      };
    });
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const Dropdown = ({ options, value, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const selectedOption = options.find((option) => option.value === value);

    return (
      <div className='relative'>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className='w-full px-4 py-2 text-left bg-white border rounded-sm flex justify-between items-center text-[13px] font-rubik'
        >
          <span>{selectedOption?.label || value}</span>
          <svg
            className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M19 9l-7 7-7-7'></path>
          </svg>
        </button>
        {isOpen && (
          <div className='absolute z-10 w-full mt-1 bg-white border rounded-sm shadow-sm'>
            {options.map((option) => (
              <button
                key={option.value}
                onClick={() => {
                  onChange(option);
                  setIsOpen(false);
                }}
                className={`text-[13px] font-rubik block w-full px-4 py-2 text-left hover:bg-gray-100 hover:text-inaqgold ${
                  value === option.value ? "bg-gray-100" : ""
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <Layout>
        <div className='bg-white'>
          <div className='container px-4 py-8'>
            <h1 className='text-5xl text-gray-500 font-bold mb-8'>Blog</h1>
            <div className='mb-12'>
              <div className='flex flex-row bg-white'>
                <div className='w-1/2 pr-2'>
                  <div className='w-full aspect-square bg-gray-100'></div>
                </div>
                <div className='w-1/2 pl-2'>
                  <div className='h-5 bg-gray-100 rounded w-1/3 mb-3'></div>
                  <div className='h-6 bg-gray-100 rounded w-full mb-3'></div>
                  <div className='space-y-2 mb-4'>
                    <div className='h-4 bg-gray-100 rounded w-full'></div>
                    <div className='h-4 bg-gray-100 rounded w-full'></div>
                    <div className='h-4 bg-gray-100 rounded w-3/4'></div>
                  </div>
                  <div className='h-4 bg-gray-100 rounded w-1/3'></div>
                </div>
              </div>
            </div>
            <div className='grid grid-cols-2 gap-4'>
              {[...Array(4)].map((_, index) => (
                <div key={index} className='mb-6'>
                  <div className='w-full aspect-square bg-gray-100 mb-4'></div>
                  <div className='h-4 bg-gray-100 rounded w-1/3 mb-2'></div>
                  <div className='h-5 bg-gray-100 rounded w-full mb-2'></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <>
      {isPreviewMode() && <PreviewBanner />}
      <Layout>
      {/* Add CSS styles for category sections */}
      <style jsx>{`
        .blog-category-section h3 {
          font-size: 2.2rem !important;
          line-height: 3.9rem !important;
        }

        .blog-category-item .blog-overlay-content .blog-overlay-categories,
        .blog-category-item .blog-overlay-content .blog-overlay-excerpt,
        .blog-category-item .blog-overlay-content .blog-overlay-description {
          display: none !important;
        }

        .blog-category-item .blog-overlay-content {
          padding: 0.5rem !important;
        }
      `}</style>

      <div className='py-[130px] bg-lightgray overflow-hidden lg:py-[90px] md:py-[75px] sm:py-[50px]'>
        <div className='container'>
          {/* Featured post section */}
          {featuredPost && featuredPost.length > 0 && showFeaturedPost && (
            <div className='lg:mb-24 mb-32 xs:mb-16 xs:mt-8 mt-10 sm:px-0 xs:pt-12'>
              <BlogSideImage
                data={featuredPost}
                animation={{
                  initial: { opacity: 0 },
                  animate: { opacity: 1 },
                }}
                link='/blog/'
                pagination={false}
              />
            </div>
          )}

          <div
            className='flex items-center justify-between my-0 sm:flex-col md:gap-6'
            id='blog-content-list'
          >
            <h3 className='font-[Oxanium] text-darkgray font-bold tracking-[-1px] md:text-center md:m-0 blog-heading-h3'>
            {t("chrome.latestHeading")}
            </h3>

            {/* Filter System */}
            <div className='flex flex-wrap gap-4 items-center'>
              <div className='w-72 xs:w-64 '>
                <Dropdown
                  options={categoryOptions}
                  value={selectedCategory}
                  onChange={handleCategoryChange}
                />
              </div>
              <div className='w-40'>
                <Dropdown
                  options={sortOptions}
                  value={sortBy}
                  onChange={handleSortChange}
                />
              </div>
              <button
                onClick={clearAllFilters}
                className='border-[4px] border-solid border-inaqgold btn-md font-normal font-rubik bg-inaqgold text-white py-2.5 px-4 rounded-sm btn btn-secondary active:bg-inaqgold focus:!bg-inaqgold btn-slide'
                style={{ "--text-color": "#fff" }}
              >
            {t("chrome.filterClear")}
              </button>
            </div>
          </div>

          {/* Active Filter Tags */}
          {activeFilters.length > 0 && (
            <div className='flex flex-wrap gap-2 mb-8'>
              {activeFilters.map((filter, index) => (
                <div
                  key={index}
                  className='flex items-center bg-inaqgold text-white sm:mt-4 px-3 py-1 rounded-sm text-sm'
                >
                  <span>{categoryOptions.find((item) => item.value === filter.value)?.label || filter.value}</span>
                  <button
                    onClick={() => removeFilter(filter)}
                    className='ml-2 focus:outline-none'
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      className='h-4 w-4'
                      viewBox='0 0 20 20'
                      fill='currentColor'
                    >
                      <path
                        fillRule='evenodd'
                        d='M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z'
                        clipRule='evenodd'
                      />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Blog posts display */}
          <div className={activeFilters.length > 0 ? "mt-4" : "mt-16"}>
            {filteredPosts.length === 0 ? (
              <div className='text-center py-8'>
                <p className='text-lg text-gray-600'>
              {t("chrome.noResults")}
                </p>
              </div>
            ) : (
              <div className='grid grid-cols-3 sm:grid-cols-2 xs:grid-cols-1 gap-6'>
                {cleanPostsForComponent(currentPosts).map((post) => (
                  <div key={post._id} className='blog-item mb-8'>
                    <BlogOverlayImg
                      data={[post]}
                      filter={false}
                      pagination={false}
                      overlay='#232323cc'
                    />
                  </div>
                ))}

                {currentPosts.length < postsPerPage &&
                  Array(postsPerPage - currentPosts.length)
                    .fill(null)
                    .map((_, i) => (
                      <div
                        key={`empty-${i}`}
                        className='blog-item mb-8 invisible'
                      ></div>
                    ))}
              </div>
            )}
          </div>

          {/* Pagination */}
          {filteredPosts.length > postsPerPage && (
            <div className='flex justify-center mt-16'>
              <Pagination
                currentPage={currentPage}
                totalPages={Math.ceil(filteredPosts.length / postsPerPage)}
                onPageChange={handlePageChange}
              />
            </div>
          )}

          {/* Category Sections - Show latest 3 articles from each category */}
          {Object.keys(categoryPosts).map((categoryName) => (
            <div key={categoryName} className='mt-4 blog-category-section'>
              <h3 className='font-[Oxanium] text-darkgray font-bold tracking-[-1px] md:text-center md:m-0 blog-heading-h3 mb-8'>
                {categoryOptions.find((item) => item.value === categoryName)?.label || categoryName}
              </h3>
              <div className='grid grid-cols-3 sm:grid-cols-2 xs:grid-cols-1 gap-6'>
                {cleanPostsForComponent(categoryPosts[categoryName]).map(
                  (post) => (
                    <div
                      key={post._id}
                      className='blog-item blog-category-item mb-8'
                    >
                      <BlogOverlayImg
                        data={[post]}
                        filter={false}
                        pagination={false}
                        overlay='#232323cc'
                      />
                    </div>
                  )
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Layout>
    </>
  );
};

export default BlogPage;
